import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFilterContext } from '../../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../../hooks/useDimensions';
import { useIsOverflow } from '../../../hooks/useIsOverflow';
import { CheckboxGrouped } from '../../Checkbox';
import Radio from '../../Radio';
import {
  Container,
  ContentWrapper,
  EmptyOptionsClearButton,
  EmptyOptionsSubTitle,
  EmptyOptionsTitle,
  EmptyOptionsViewContainer,
  InputWrapper,
  ItemsContainer,
  ItemsCount,
  ItemsTitle,
  NoResultsText,
  ScrollableContainer,
  ShimmerContainer,
  ShimmerOptionRowContainer,
  ShimmerOptionsContainer,
  ShimmerOptionsIconAndLabel,
  StyledCheckboxGrouped,
  StyledSearchInput,
  StyledShimmerTitle,
} from './SelectionFilter.styles';
import { createArrayCopy, filterValuesByText } from './util';

export interface SelectFiltersProps<T> {
  values: Value<T>[];
  amountOfItemsInPreview?: number;
  multiselect?: boolean;
  loading?: boolean;
  searchInputPlaceholder?: string;
  reverseCheckboxOrder?: boolean;
  displayCount?: boolean;
  checkboxSizeInPx?: number;
  onChange: (...args: any) => void;
  noPadding?: boolean;
  menuMaxHeight?: string;
}

export interface Value<T = any> {
  selected: boolean;
  value: T;
  label: string;
  data?: any;
  partiallySelected?: boolean;
}

const NoSearchResultsComponent = (props: { searchText: string }) => {
  const { t } = useTranslation();

  return <NoResultsText>{t('filters.noResultsFor', { param: props.searchText })}</NoResultsText>;
};

export const SelectionFilterShimmer = () => {
  const { width } = useWindowDimensions();

  return (
    <Container $shouldExpandToMaxHeight={inMobile(width) ? true : false}>
      <ShimmerContainer>
        <InputWrapper>
          <StyledShimmerTitle $flex="1" $height={inMobile(width) ? '2.5rem' : '2rem'} $borderRadius={'2.125rem'} />
        </InputWrapper>
        <ScrollableContainer $isOverflow={true}>
          <ShimmerOptionsContainer>
            {[...Array(inMobile(width) ? 10 : 6)].map(() => {
              return (
                <ShimmerOptionRowContainer>
                  <ShimmerOptionsIconAndLabel>
                    {!inMobile(width) && <StyledShimmerTitle $width="1rem" $height="1rem" $borderRadius="0.25rem" />}
                    <StyledShimmerTitle
                      $width={inMobile(width) ? '9.98rem' : '7.75rem'}
                      $height="1rem"
                      $borderRadius="0.5rem"
                    />
                  </ShimmerOptionsIconAndLabel>
                  <StyledShimmerTitle
                    $width={inMobile(width) ? '1.5rem' : '1rem'}
                    $height={inMobile(width) ? '1.5rem' : '1rem'}
                    $borderRadius="0.25rem"
                  />
                </ShimmerOptionRowContainer>
              );
            })}
          </ShimmerOptionsContainer>
        </ScrollableContainer>
      </ShimmerContainer>
    </Container>
  );
};

const SelectionFilter = <T,>({
  values,
  amountOfItemsInPreview = values?.length,
  multiselect,
  loading,
  searchInputPlaceholder,
  reverseCheckboxOrder = false,
  displayCount = true,
  checkboxSizeInPx = 16,
  menuMaxHeight = '14.75rem',
  onChange,
  noPadding,
}: SelectFiltersProps<T>) => {
  // Dependencies
  const { t } = useTranslation();
  const { clearAllFilters, setOpenFiltersMobile, setSelectedFilterTitle } = useFilterContext();

  // Reference
  const scrollContainerRef = useRef();

  // State
  const [searchText, setSearchText] = React.useState<string>('');
  const [valuesToDisplay, setValuesToDisplay] = React.useState(createArrayCopy(values));

  // Derived State
  const radioValue = valuesToDisplay?.find((item) => item.selected === true)?.value;
  const checkboxValues = valuesToDisplay?.filter((item) => item.selected === true).map((item) => item.value);
  const filteredItems = filterValuesByText(valuesToDisplay, searchText);
  const isListOverflowed = useIsOverflow(scrollContainerRef);
  const showEmptySearch = searchText.length > 0 && filteredItems?.length === 0;
  const noAvailableOptions = !loading && values?.length === 0;

  // Effects
  React.useEffect(() => {
    setValuesToDisplay(createArrayCopy(values));
  }, [values]);

  // Handlers
  const handleTextChange = (text: string) => {
    setSearchText(text);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
    setOpenFiltersMobile(false);
    setSelectedFilterTitle('');
  };

  const handleCheckboxChange = (selected: boolean, item: Value<T>) => {
    // If the item was in indeterminate state, we want to force it to selected state
    if (item.partiallySelected) {
      const updatedItem = {
        ...item,
        partiallySelected: false,
        selected: true,
      };
      onChange(true, updatedItem.value);
    } else {
      // Normal checkbox behavior
      onChange(selected, item.value);
    }
  };

  return (
    <ContentWrapper>
      {(loading || !values) && <SelectionFilterShimmer />}
      {!loading && noAvailableOptions && (
        <EmptyOptionsViewContainer>
          <EmptyOptionsTitle>{t('filters.noOptionsAvailable')}</EmptyOptionsTitle>
          <EmptyOptionsSubTitle>{t('filters.itSeemsNoOptionsAvailable')}</EmptyOptionsSubTitle>
          <EmptyOptionsClearButton onClick={handleClearAllFilters}>
            {t('filters.clearYourCurrentFilters')}
          </EmptyOptionsClearButton>
        </EmptyOptionsViewContainer>
      )}
      {
        <>
          {multiselect ? (
            <Container
              $shouldHideContent={loading || noAvailableOptions || !values}
              $shouldExpandToMaxHeight={searchInputPlaceholder?.length > 0}
              $noPadding={noPadding}
              $isOverflow={isListOverflowed}
            >
              {searchInputPlaceholder?.length > 0 ? (
                <InputWrapper>
                  <StyledSearchInput onTextChange={handleTextChange} placeholder={searchInputPlaceholder} />
                </InputWrapper>
              ) : null}
              <ScrollableContainer
                ref={scrollContainerRef}
                $isOverflow={isListOverflowed}
                $noPadding={noPadding}
                $maxHeight={menuMaxHeight}
              >
                {showEmptySearch ? (
                  <NoSearchResultsComponent searchText={searchText} />
                ) : (
                  <CheckboxGrouped.Group aria-label="options-container" value={checkboxValues}>
                    <ItemsContainer>
                      {filteredItems?.slice(0, amountOfItemsInPreview).map((item, index) => (
                        <StyledCheckboxGrouped
                          key={index}
                          $reverseCheckboxOrder={reverseCheckboxOrder}
                          $checkboxSizeInPx={checkboxSizeInPx}
                          $isSelected={!item.partiallySelected && item.selected}
                          value={!item.partiallySelected && item.value}
                          onChange={(selected) => handleCheckboxChange(selected, item)}
                          isIndeterminate={item.partiallySelected}
                        >
                          <ItemsTitle>{item.label}</ItemsTitle>
                          {displayCount ? <ItemsCount>{item?.data}</ItemsCount> : null}
                        </StyledCheckboxGrouped>
                      ))}
                    </ItemsContainer>
                  </CheckboxGrouped.Group>
                )}
              </ScrollableContainer>
            </Container>
          ) : (
            <ScrollableContainer $shouldHideContent={loading || noAvailableOptions || !values}>
              <Radio.Group onChange={(value) => onChange(value)} value={radioValue}>
                {filteredItems?.slice(0, amountOfItemsInPreview).map((item) => (
                  <Radio value={item.value} description={item.data}>
                    <ItemsTitle addPadding>{item.label}</ItemsTitle>
                  </Radio>
                ))}
              </Radio.Group>
            </ScrollableContainer>
          )}
        </>
      }
    </ContentWrapper>
  );
};

export default SelectionFilter;
